import styled from "@emotion/styled";
import { format } from "date-fns";
import { PDFDocument } from "pdf-lib";
import {
  Box,
  Chip as MuiChip,
  CircularProgress,
  Container,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "../../../../hooks/useAuth";
import DocumentViewer from "../../../../components/viewers/DocumentViewer";
import AIContractInformation from "../components/ContractMetadata";
import AIContractSigneeManagement from "../components/AIContractSigneeManagement";
import { ReactComponent as Logo } from "../../../../vendor/logo.svg";
import { ISignature, ISignee } from "../../../../types/signature";
import { createFileFromUrl } from "../../../../utils/file";
import {
  useAddContractAISignatureMutation,
  useAddSignatureDocumentContentMutation,
  useGetContractAISignatureQuery,
} from "../../../../redux/slices/digitalSigningSlice";
import { DialogMode } from "../../../../types/dialogmode";
import ContractHeader from "../components/ContractHeader";

import RejectDialog from "./RejectDialog";
import SigningAppBar from "./SigningAppBar";
import useTheme from "../../../../hooks/useTheme";
import { THEMES } from "../../../../constants";

import { useDispatch } from "react-redux";
import { addFile } from "../../../../redux/slices/filesSlice";

// function getFileTypeName(mimeType: string) {
//   const mimeEntry = mimeDb[mimeType];
//   if (mimeEntry && mimeEntry.extensions) {
//     const fileExtension = mimeEntry.extensions[0];
//     return fileExtension.toUpperCase();
//   }
//   return "Unknown";
// }

const Brand = styled(Logo)`
  fill: ${(props) => props.theme.palette.primary.main};
  width: 128px;
  height: auto;
  margin-bottom: 24px;
  cursor: pointer;
`;

const Chip = styled(MuiChip)<{ color?: string }>`
  // font-size: 85%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"]}; // .light
  // color: ${(props) => props.theme.palette.common.white};
`;

const Spacer = styled.div`
  flex: 1 1 100%;
`;

const AIContractSign = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [file, setFile] = useState<any>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [signer, setSigner] = useState<ISignee>();
  const [signees, setSignees] = useState<Array<ISignee>>([]);
  const [signable, setSignable] = useState(
    !!(searchParams.get("code") && searchParams.get("hex"))
  );
  const [signatures, setSignatures] = useState<Array<ISignature>>([]);
  const [toggleSignaturePad, setToggleSignaturePad] = useState<
    boolean | undefined
  >();
  const [signatureSizePercentValue, setSignatureSizePercentValue] = useState({
    width: 0,
    height: 0,
  });

  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const { setTheme, theme } = useTheme();

  // Since this is an external signing page, we force the light theme
  useEffect(() => {
    if (theme === THEMES.DARK) {
      setTheme(THEMES.LIGHT);
    }
  }, [theme]);

  const {
    data: contractDetails,
    isLoading: contractDetailLoading,
    isSuccess: contractDetailLoaded,
    isFetching: contractDetailFetching,
    refetch: contractDetailRefetch,
    error,
  } = useGetContractAISignatureQuery({
    code: searchParams.get("code"),
    hex: searchParams.get("hex"),
  });

  // const {
  //   data: arrayBuffer,
  //   isSuccess: fileLoaded,
  //   isLoading: gettingDocumentContent,
  // } = useGetDocumentContentQuery(contractDetail?.document, {
  //   skip: !contractDetail?.document,
  // });

  const [
    addContractAISignature,
    { isLoading: isAdding, isSuccess: addSignSuccess, data: addedSignature },
  ] = useAddContractAISignatureMutation();

  const [
    addDocument,
    {
      isLoading: isUploading,
      isSuccess: documentUploaded,
      reset: resetContent,
    },
  ] = useAddSignatureDocumentContentMutation();

  useEffect(() => {
    if (error && error.status === 410) {
      navigate(
        `/410?page=signature&code=${searchParams.get(
          "code"
        )}&hex=${searchParams.get("hex")}`
      );
    } else if (error) {
      // navigate to the signing link expired page

      navigate(`/${error.status}`);
    }
  }, [error]);

  useEffect(() => {
    if (contractDetails?.documentContent) {
      // add the file to the store
      addFile({
        id: contractDetails?.document?.id || "",
        buffer: contractDetails.documentContent,
      });

      // const blob = new Blob([contractDetails.documentContent as any], {
      //   type: "application/pdf",
      // });
      // const url = URL.createObjectURL(blob);
      // console.log(url);

      // setFileUrl(url);
    } else {
      setFileUrl(null);
    }
  }, [contractDetails?.documentContent]);

  useEffect(() => {
    if (addSignSuccess && addedSignature) {
      console.log("addedSignature: ", addedSignature);

      const formData = new FormData();
      // Append the File object to the FormData object
      formData.append("file", file);
      formData.append("email", addedSignature.email);
      formData.append("displayName", addedSignature.displayName);
      formData.append("contractName", contractDetails?.metadata?.name);
      addDocument({
        contractId: addedSignature?.contractId,
        documentId: addedSignature?.documentId,
        file: formData,
        type: "signed",
      });

      // update the signer's hasSigned status
      handleSaveSignature();
    }
  }, [addSignSuccess, addedSignature]);

  useEffect(() => {
    if (contractDetailLoaded && contractDetails) {
      const { dataDecrypted, signatures: _signatures } = contractDetails;

      const signatureId = _signatures.find(
        (sig: ISignature) => sig.email === dataDecrypted.email
      )?.id;

      setSigner({
        displayName: dataDecrypted.displayName,
        email: dataDecrypted.email,
        hasSigned: false,
        signatureId,
      });

      setSignees(
        _signatures.map((sig: ISignature) => {
          const signee: ISignee = {
            displayName: sig.displayName,
            email: sig.email,
            hasSigned: sig.isSigned,
          };

          return signee;
        })
      );

      setSignatures(_signatures);
      // if (contractDetail?.blob) {
      //   const blob = new Blob([contractDetail?.blob.data], { type: "application/pdf" });
      //   const url = URL.createObjectURL(blob);
      //   setFileUrl(url);
      // } else {
      //   setFileUrl(null);
      // }
    }
  }, [contractDetailLoaded]);

  useEffect(() => {
    setSignable(!!(searchParams.get("code") && searchParams.get("hex")));
  }, [searchParams.get("code"), searchParams.get("hex")]);

  const handleSaveSignature = () => {
    setSignees((prev) =>
      prev.map((signee) => {
        if (
          signee.email === signer?.email &&
          signee.email === addedSignature?.email
        ) {
          signee.hasSigned = addedSignature?.isSigned;
          signee.isApproved = addedSignature?.isApproved;
        }

        return signee;
      })
    );

    setSigner((prev) => {
      if (prev) {
        const signee: ISignee = {
          ...prev,
          hasSigned:
            signer?.email === addedSignature?.email && addedSignature?.isSigned,
          isApproved: addedSignature?.isApproved,
        };

        return signee;
      }
    });
  };

  useEffect(() => {
    if (
      !addSignSuccess &&
      signatureSizePercentValue.width &&
      signatureSizePercentValue.height
    ) {
      setTimeout(() => {
        addSignatureToPDF();
      }, 10);
    }
  }, [signer, signatureSizePercentValue]);

  const addSignatureToPDF = async () => {
    let userIndex = 0;
    const userSignature = signatures.find((sig, index) => {
      if (sig.email === signer?.email && signer.signatureId === sig.id) {
        userIndex = index;
      }
      return sig.email === signer?.email;
    });

    console.log({ userSignature });
    if (userSignature && userSignature?.signatureUrl) {
      const signatureFile = await createFileFromUrl(
        userSignature?.signatureUrl,
        contractDetails?.metadata?.name,
        "image/png"
      );
      if (
        signatureFile &&
        userSignature.signedDate &&
        contractDetails?.documentContent
      ) {
        const pdfBytes = new Uint8Array(
          (
            contractDetails.documentContent as {
              data: ArrayBuffer;
              type: string;
            }
          )?.data
        );

        const signatureBytes = await signatureFile.arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const numPages = await pdfDoc.getPageCount();
        const pageHeightPercent = 1 / numPages;
        const pageIndex =
          userSignature.positionPercentValue.y > pageHeightPercent
            ? Math.floor(
                userSignature.positionPercentValue.y / pageHeightPercent
              )
            : 0;
        const page = await pdfDoc.getPage(pageIndex);
        const signatureImage = await pdfDoc.embedPng(signatureBytes);
        const { width, height } = page.getSize();
        // const signatureWith = 150;
        // const signatureHeight = 50;
        const signatureWidth = signatureSizePercentValue.width * width;
        const signatureHeight = signatureSizePercentValue.height * height;

        // calculate coordinate difference:
        // 180: width of signature box
        // 630: width of wrapper (div have position relative) of signature boxs
        const deviation = (180 / 630) * userIndex;

        // console.table({
        //   signatureWith,
        //   width,
        //   userIndex,
        //   deviation,
        //   dif: signatureWith / width,
        // });

        const signatureX =
          width * (userSignature.positionPercentValue.x + deviation);
        const signatureY =
          userSignature.positionPercentValue.y > pageHeightPercent
            ? height -
              height *
                (userSignature.positionPercentValue.y -
                  pageHeightPercent * pageIndex) *
                numPages -
              signatureHeight
            : height -
              height * userSignature.positionPercentValue.y * numPages -
              signatureHeight;
        const name = userSignature.displayName;
        const nameSize = 11;
        const nameX = signatureX;
        const nameY = signatureY - nameSize - 10;
        const date = format(new Date(userSignature.signedDate), "PPPPp zzzz");
        const dateSize = 8;
        const dateX = signatureX;
        const dateY = nameY - dateSize - 4;

        page.drawImage(signatureImage, {
          x: signatureX,
          y: signatureY,
          width: signatureWidth,
          height: signatureHeight,
        });

        page.drawText(name, {
          x: nameX,
          y: nameY,
          size: nameSize,
        });

        page.drawText(date, {
          x: dateX,
          y: dateY,
          size: dateSize,
        });

        if (userSignature.reason) {
          const reason = userSignature.reason;
          const reasonSize = 8;
          const reasonX = signatureX;
          const reasonY = dateY - reasonSize - 4;
          page.drawText(reason, {
            x: reasonX,
            y: reasonY,
            size: reasonSize,
          });
        }

        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
        new Response(blob).arrayBuffer().then((arrayBuffer) => {
          dispatch(
            addFile({ id: userSignature?.id || "", buffer: arrayBuffer })
          );
        });

        const url = URL.createObjectURL(blob);
        setFileUrl(url);

        console.log({ fileWithSignature: url });

        const newFile = await createFileFromUrl(
          url,
          contractDetails?.metadata?.name,
          "application/pdf"
        );
        console.log(URL.createObjectURL(newFile));
        setFile(newFile);

        addContractAISignature({
          contractId: contractDetails?.metadata?.id,
          signatureId: userSignature.id,
          code: searchParams.get("code"),
          hex: searchParams.get("hex"),
          isApproved: userSignature.isApproved,
          reason: userSignature.reason,
        });
      }
    }
  };

  const handleSignButton = () => {
    if (setToggleSignaturePad) {
      setToggleSignaturePad((prev) => !prev);
    }
  };

  const onReject = (reason: string) => {
    console.log("Rejecting: ", reason);

    // create a new canvas element that shows the rejected stamp
    const offscreenCanvas = document.createElement("canvas");
    offscreenCanvas.width = 180;
    offscreenCanvas.height = 75;

    // add the img to the canvas
    const img = new Image();
    img.width = 200;
    img.height = 93;
    img.src = "/static/img/RejectedStamp.png";

    img.onload = () => {
      const ctx = offscreenCanvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, offscreenCanvas.width, offscreenCanvas.height);

      const dataUrl = offscreenCanvas.toDataURL();

      if (setSignatures) {
        setSignatures((prev) => {
          const newSignatures = prev.map((sig) => {
            if (sig.email === signer?.email) {
              return {
                ...sig,
                signatureUrl: dataUrl,
                signedDate: new Date().toISOString(),
                reason: reason,
                isSigned: true,
                isApproved: false,
              };
            } else {
              return { ...sig };
            }
          });

          return newSignatures;
        });
      }

      setShowRejectDialog(false);
    };
  };

  return (
    <>
      <SigningAppBar
        contractDetail={contractDetails?.metadata}
        handleSignButton={handleSignButton}
        handleRejectButton={() => setShowRejectDialog(true)}
        signable={signable}
        signature={signatures?.find((sig) => sig.email === signer?.email)}
        signer={signer}
        loading={isAdding || isUploading}
      />

      <Container maxWidth="lg">
        {contractDetailLoading ? (
          <Box textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <Paper
            sx={{
              marginTop: 10,
              marginBottom: 60,
              paddingLeft: 4,
              paddingRight: 4,
              paddingTop: 4,
            }}
          >
            {contractDetails?.metadata && (
              <Box mb={4}>
                <ContractHeader
                  contract={{ ...contractDetails?.metadata, status: null }}
                />
              </Box>
            )}

            {contractDetails?.documentContent && signer && (
              <Grid
                container
                justifyContent="space-between"
                flexDirection="row"
              >
                <Grid size={{ xs: 12, md: 8 }} mb={6}>
                  <Box>
                    <AIContractInformation
                      contract={contractDetails?.metadata}
                    />
                  </Box>

                  <DocumentViewer
                    document={contractDetails?.document}
                    hideFileMenu
                    viewMode="pageOnly"
                    signable={signable}
                    signer={signer}
                    signatures={signatures}
                    setSignatures={setSignatures}
                    onSaveSignature={handleSaveSignature}
                    setSignatureSizePercentValue={setSignatureSizePercentValue}
                    toggleSignaturePad={toggleSignaturePad}
                    disableAppBar
                    disableScroller
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box flex={1}>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        mb: 6,
                        py: 6,
                        px: 3,
                        backgroundColor: "background.default",
                      }}
                    >
                      <AIContractSigneeManagement
                        signer={signer}
                        signable={signable}
                        signees={signees}
                        setSignees={setSignees}
                        setSignatures={setSignatures}
                        signatures={signatures}
                        setToggleSignaturePad={setToggleSignaturePad}
                        contract={contractDetails?.metadata}
                        document={contractDetails?.document}
                        mode={DialogMode.Edit}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}
      </Container>

      <RejectDialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onReject={onReject}
      />
    </>
  );
};

export default AIContractSign;
